import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'designSystem/components/HoverCard'
import { useOptionalSidePanelPresentation } from 'designSystem/components/sidePanelContext'
import { Text } from 'designSystem/components/Text'
import {
  datapointCitationLabel,
  formatCitationTimestamp,
  formatDatapointValue,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/datapointValueCitations'
import type {
  DatapointValueCitation,
  TimeSeriesComputedSeries,
  TimeSeriesSeriesSummary,
  TimeSeriesTrendRef,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import {
  plainPenIds,
  type PreviewTrendHandler,
  type TrendPreview,
  trendPreviewFromRef,
  type TrendPreviewMarker,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreview'
import { isComputedColumnId } from 'features/timeSeries/computed/computedTrendStore'
import { Activity } from 'lucide-react'
import React, { useCallback } from 'react'

const FALLBACK_WINDOW_PADDING_MS = 6 * 60 * 60 * 1000

function citationMarker(
  citation: DatapointValueCitation
): TrendPreviewMarker | undefined {
  if (citation.value === null || citation.value === undefined) return undefined
  if (!citation.timestamp) return undefined
  return {
    entryId: citation.entry_id,
    timestamp: citation.timestamp,
    value: citation.value,
    requestedAt: Date.now(),
  }
}

function paddedFallbackWindow(
  timestamp: string | null | undefined
): { startDatetime: string; endDatetime: string } | undefined {
  if (!timestamp) return undefined
  const parsed = Date.parse(timestamp)
  if (!Number.isFinite(parsed)) return undefined
  return {
    startDatetime: new Date(parsed - FALLBACK_WINDOW_PADDING_MS).toISOString(),
    endDatetime: new Date(parsed + FALLBACK_WINDOW_PADDING_MS).toISOString(),
  }
}

/** The span a cited series covers: its first bin through the end of its last one, or the padded fallback for a single point. */
function seriesWindow(
  series: TimeSeriesComputedSeries
): { startDatetime: string; endDatetime: string } | undefined {
  const stamps = series.points.map(([stamp]) => Date.parse(stamp))
  if (stamps.length < 2 || !stamps.every(Number.isFinite)) {
    return paddedFallbackWindow(series.points[0]?.[0])
  }
  const last = stamps[stamps.length - 1]
  const bin = last - stamps[stamps.length - 2]
  return {
    startDatetime: new Date(stamps[0]).toISOString(),
    endDatetime: new Date(last + bin).toISOString(),
  }
}

function withPen(base: TrendPreview, entryId: string): TrendPreview {
  return {
    ...base,
    entryIds: [...new Set([...base.entryIds, entryId])],
    contextEntryIds: [...new Set([...plainPenIds(base), entryId])],
  }
}

interface ResponseSegmentValueChipProps {
  citation: DatapointValueCitation
  summary?: TimeSeriesSeriesSummary
  /** Points for a cited computed or totalizer column, so the chip can plot it. */
  series?: TimeSeriesComputedSeries
  trendRef?: TimeSeriesTrendRef
  onCitationHoverChange?: (highlighted: boolean) => void
  onPreviewTrend?: PreviewTrendHandler
}

export function ResponseSegmentValueChip({
  citation,
  summary,
  series,
  trendRef,
  onCitationHoverChange,
  onPreviewTrend,
}: ResponseSegmentValueChipProps): React.ReactElement {
  useLingui()
  const sidePanel = useOptionalSidePanelPresentation()
  const label = datapointCitationLabel(citation)
  const valueText = formatDatapointValue(citation.value, citation.units)
  const timestampText = formatCitationTimestamp(citation.timestamp)
  const stats = summary?.stats
  const statsText = stats
    ? (() => {
        const min = formatDatapointValue(stats.min, citation.units)
        const mean = formatDatapointValue(stats.mean, citation.units)
        const max = formatDatapointValue(stats.max, citation.units)
        return t`window: min ${min} · mean ${mean} · max ${max}`
      })()
    : null
  const isColumn = isComputedColumnId(citation.entry_id)
  const canOpenTrend =
    Boolean(onPreviewTrend) &&
    (!isColumn || Boolean(series) || Boolean(trendRef))

  const handleClick = useCallback(() => {
    if (!onPreviewTrend) return
    sidePanel?.keepOpenOnNextOutsideClick()
    const marker = citationMarker(citation)
    const base = trendRef ? trendPreviewFromRef(trendRef) : undefined
    if (!isColumn) {
      onPreviewTrend(
        base
          ? { ...withPen(base, citation.entry_id), marker }
          : {
              entryIds: [citation.entry_id],
              label,
              ...paddedFallbackWindow(citation.timestamp),
              marker,
            }
      )
      return
    }
    const plotted = base?.computedSeries?.some(
      (candidate) => candidate.entry_id === citation.entry_id
    )
    if (base && plotted) {
      onPreviewTrend({ ...base, marker })
      return
    }
    if (!series) return
    onPreviewTrend({
      ...(base ?? {
        entryIds: [],
        contextEntryIds: [],
        label,
        ...seriesWindow(series),
      }),
      computedSeries: [...(base?.computedSeries ?? []), series],
      marker,
    })
  }, [citation, isColumn, label, onPreviewTrend, series, sidePanel, trendRef])

  return (
    <HoverCard onOpenChange={onCitationHoverChange}>
      <HoverCardTrigger asChild>
        <Button
          type='button'
          variant='outline'
          size='xs'
          onClick={canOpenTrend ? handleClick : undefined}>
          <Activity />
          {`${label} · ${valueText}`}
        </Button>
      </HoverCardTrigger>
      <HoverCardContent side='bottom'>
        <Box display='flex' direction='column' gap='xs'>
          <Text variant='caption' weight='medium'>
            {citation.name ?? label}
          </Text>
          <Text variant='caption'>
            {citation.aggregation_op
              ? `${citation.aggregation_op} · ${valueText}`
              : valueText}
          </Text>
          {timestampText ? (
            <Text variant='caption' color='muted'>
              <Trans>as of {timestampText}</Trans>
            </Text>
          ) : null}
          {statsText ? (
            <Text variant='caption' color='muted'>
              {statsText}
            </Text>
          ) : null}
          {canOpenTrend ? (
            <Text variant='caption' color='muted'>
              {trendRef ? (
                <Trans>Click to open the chart for this answer.</Trans>
              ) : isColumn ? (
                <Trans>Click to put this series on the chart.</Trans>
              ) : (
                <Trans>Click to put this tag on the chart.</Trans>
              )}
            </Text>
          ) : null}
        </Box>
      </HoverCardContent>
    </HoverCard>
  )
}
